// Plain words for things the api names technically (roles, listen modes, talks). Display only: nothing here
// changes what is stored or sent, except talkModeToListenMode, which maps a control back to a listen mode.
import { ROLES, type ConversationRole, type ListenMode } from '@jabberr/core';

/** Where the web says it differently from ROLES (core keeps its labels for the api and the iOS app). */
const ROLE_WORDS: Partial<Record<ConversationRole, string>> = {
  open: 'Open thinking',
};

export function roleLabel(role: ConversationRole): string {
  return ROLE_WORDS[role] ?? ROLES[role].label;
}

/** The roles a team member can pick for a conversation, in the order the menu shows them. */
export function pickableRoles(salesEnabled: boolean): ConversationRole[] {
  // client_partner stays pickable so the team can try what a client hears (it was in the old menu too).
  const base: ConversationRole[] = ['product_owner', 'open', 'marketing', 'strategy', 'client_partner'];
  return salesEnabled ? [...base, 'sales_executive', 'client_sales_executive'] : base;
}

/** "When Jabberr talks": the four supported participation modes. */
export type TalkMode = ListenMode;

export interface TalkModeOption {
  mode: TalkMode;
  label: string;
  hint: string;
}

export const TALK_MODES: readonly TalkModeOption[] = [
  { mode: 'always', label: 'Always', hint: 'Jabberr answers everything said.' },
  { mode: 'mentions', label: 'When named', hint: 'Jabberr answers when someone says his name.' },
  { mode: 'keen', label: 'Keen', hint: 'Jabberr speaks when he has something useful.' },
  { mode: 'silent', label: 'Silent', hint: 'Jabberr only listens and keeps notes.' },
];

/** The listen mode a choice sets. */
export function talkModeToListenMode(mode: TalkMode): ListenMode {
  return mode;
}

export function talkModeLabel(mode: ListenMode): string {
  return TALK_MODES.find((m) => m.mode === mode)?.label ?? mode;
}

export function talkModeHint(mode: ListenMode): string {
  return TALK_MODES.find((m) => m.mode === mode)?.hint ?? '';
}

/** A talk is a meeting with only you and Jabberr, started from "Talk with Jabberr" or "Talk instead". */
export const TALK_TITLE = 'Talk with Jabberr';

export function talkTitleFor(conversationTitle: string | null | undefined): string {
  const t = (conversationTitle ?? '').trim();
  return t ? `Talk: ${t}`.slice(0, 200) : TALK_TITLE;
}

export function isTalk(meeting: { title: string }): boolean {
  return meeting.title === TALK_TITLE || meeting.title.startsWith('Talk: ');
}

/** The thinking setting in words ("effort" is the engine's name for it). */
export function thinkingLabel(effort: 'low' | 'high' | string): string {
  return effort === 'high' ? 'Thinks longer' : 'Quick';
}

/** First names, for "With the team · Adam, Chris, John". */
export function firstNames(names: (string | null | undefined)[], max = 4): string {
  const firsts = names
    .map((n) => (n ?? '').trim().split(/\s+/)[0] ?? '')
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  if (firsts.length <= max) return firsts.join(', ');
  return `${firsts.slice(0, max).join(', ')} and ${firsts.length - max} more`;
}

/** "1.3.0" from a production state document's first lines, when it names a version. Display only. */
export function liveVersion(content: string | null | undefined): string | null {
  const head = (content ?? '').split('\n').slice(0, 3).join(' ');
  const match = /\bv?(\d+\.\d+(?:\.\d+)?)\b/.exec(head);
  return match?.[1] ?? null;
}
